import {
  statusMapParser,
  statusValues,
} from '@bsa/scouting-js-utils/src/constants/advancements';
import { StatusProp } from 'scouting-ui/lib/StatusTag/StatusTag';
import type { ColorType } from 'scouting-ui/lib/types';

/**
 * The advancements API sends status as a lowercase string ("awarded",
 * "notstarted"), while the rest of the app compares against the UPPER_SNAKE
 * constants. `statusMapParser` accepts either and answers in UPPER_SNAKE;
 * anything it does not know is treated as not started.
 */
export function parseStatus(status?: string | null): string {
  return statusMapParser[String(status ?? '').toLowerCase()] ?? 'NOT_STARTED';
}

/** The `StatusTag` variant for a parsed status. */
const tagStatuses: Record<string, string> = {
  NOT_STARTED: StatusProp.NOT_STARTED,
  STARTED: StatusProp.STARTED,
  IN_PROGRESS: StatusProp.INPROGRESS,
  COMPLETED: StatusProp.COMPLETED,
  EARNED: StatusProp.COMPLETED,
  APPROVED: StatusProp.APPROVED,
  LEADER_SIGNED: StatusProp.APPROVED,
  LEADER_APPROVED: StatusProp.APPROVED,
  COUNSELOR_APPROVED: StatusProp.APPROVED,
  AWARDED: StatusProp.AWARDED,
  DISABLED: 'disabled',
  RETIRED: 'disabled',
};

export function tagStatus(status?: string | null): string {
  return tagStatuses[parseStatus(status)] ?? StatusProp.NOT_STARTED;
}

/** The progress bar and percentage color for a `StatusTag` variant. */
const statusColors: Record<string, ColorType> = {
  [StatusProp.STARTED]: 'color-orange',
  [StatusProp.INPROGRESS]: 'color-orange',
  [StatusProp.COMPLETED]: 'color-green',
  [StatusProp.APPROVED]: 'color-info',
  [StatusProp.AWARDED]: 'color-yellow',
};

export function statusColor(tag: string): ColorType {
  return statusColors[tag] ?? 'color-primary-scoutingWarmGray';
}

/** Whether the status is far enough along to show a pill and an audit tooltip. */
export function isStarted(status?: string | null): boolean {
  return statusValues[parseStatus(status)] > statusValues.STARTED;
}

/** Whether the status is far enough along that the progress bar is no longer the story. */
export function isCompleted(status?: string | null): boolean {
  return statusValues[parseStatus(status)] >= statusValues.COMPLETED;
}

/**
 * The advancements API reports completion as a fraction (0.45), while everything
 * that displays it wants a percentage (45). Floored, not rounded, so an item that
 * is a hair short of done never reads as 100%.
 */
export function toPercent(fraction?: number | null): number {
  const value = Number(fraction);
  return Number.isFinite(value) ? Math.floor(value * 100) : 0;
}
